import { offlineStorage } from './offlineStorage';
import { localCustomerStorage, localBookingStorage } from './localStorageCache';
import type { Customer, Location, Contact, Booking } from '../types/crm';

export function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

function mergeCustomerLocal(customer: Customer, op: 'add' | 'update' | 'delete'): void {
  const current = (localCustomerStorage.getData() as Customer[] | null) || [];
  let next: Customer[];
  if (op === 'add') {
    const without = current.filter((c) => c.id !== customer.id);
    next = [...without, customer];
  } else if (op === 'update') {
    next = current.map((c) => (c.id === customer.id ? customer : c));
  } else {
    next = current.filter((c) => c.id !== customer.id);
  }
  localCustomerStorage.setData(next);
}

function mergeLocationLocal(customerId: string, location: Location, op: 'add' | 'update' | 'delete'): void {
  const current = (localCustomerStorage.getData() as Customer[] | null) || [];
  const next = current.map((cust) => {
    if (cust.id !== customerId) return cust;
    const copy = { ...cust };
    const locs = copy.locations || [];
    if (op === 'add') {
      copy.locations = [...locs.filter((l) => l.id !== location.id), location];
    } else if (op === 'update') {
      copy.locations = locs.map((l) => (l.id === location.id ? location : l));
    } else {
      copy.locations = locs.filter((l) => l.id !== location.id);
    }
    return copy;
  });
  localCustomerStorage.setData(next);
}

function mergeContactLocal(customerId: string, contact: Contact, op: 'add' | 'update' | 'delete'): void {
  const current = (localCustomerStorage.getData() as Customer[] | null) || [];
  const next = current.map((cust) => {
    if (cust.id !== customerId) return cust;
    const copy = { ...cust };
    const contacts = copy.contacts || [];
    if (op === 'add') {
      copy.contacts = [...contacts.filter((c) => c.id !== contact.id), contact];
    } else if (op === 'update') {
      copy.contacts = contacts.map((c) => (c.id === contact.id ? contact : c));
    } else {
      copy.contacts = contacts.filter((c) => c.id !== contact.id);
    }
    return copy;
  });
  localCustomerStorage.setData(next);
}

function mergeBookingLocal(booking: Booking, op: 'add' | 'update' | 'delete'): void {
  const current = (localBookingStorage.getData() as Booking[] | null) || [];
  let next: Booking[];
  if (op === 'add') {
    const without = current.filter((b) => b.id !== booking.id);
    next = [...without, booking];
  } else if (op === 'update') {
    next = current.map((b) => (b.id === booking.id ? booking : b));
  } else {
    next = current.filter((b) => b.id !== booking.id);
  }
  localBookingStorage.setData(next);
}

export async function deferCustomerWriteToOfflineIfNeeded(
  operation: 'create' | 'update' | 'delete',
  customer: Customer
): Promise<boolean> {
  if (!isBrowserOffline()) return false;
  const data =
    operation === 'delete'
      ? { id: customer.id, operation: 'delete' as const }
      : { ...customer, operation };
  await offlineStorage.queueMutation({ type: 'customer', data, timestamp: Date.now() });
  mergeCustomerLocal(
    customer,
    operation === 'create' ? 'add' : operation === 'update' ? 'update' : 'delete'
  );
  return true;
}

export async function deferLocationWriteToOfflineIfNeeded(
  operation: 'create' | 'update' | 'delete',
  customerId: string,
  location: Location
): Promise<boolean> {
  if (!isBrowserOffline()) return false;
  const data =
    operation === 'delete'
      ? { customerId, location: { id: location.id } as Location, operation: 'delete' as const }
      : { customerId, location, operation };
  await offlineStorage.queueMutation({ type: 'location', data, timestamp: Date.now() });
  mergeLocationLocal(
    customerId,
    location,
    operation === 'create' ? 'add' : operation === 'update' ? 'update' : 'delete'
  );
  return true;
}

export async function deferContactWriteToOfflineIfNeeded(
  operation: 'create' | 'update' | 'delete',
  customerId: string,
  contact: Contact
): Promise<boolean> {
  if (!isBrowserOffline()) return false;
  const data =
    operation === 'delete'
      ? { customerId, contact: { id: contact.id } as Contact, operation: 'delete' as const }
      : { customerId, contact, operation };
  await offlineStorage.queueMutation({ type: 'contact', data, timestamp: Date.now() });
  mergeContactLocal(
    customerId,
    contact,
    operation === 'create' ? 'add' : operation === 'update' ? 'update' : 'delete'
  );
  return true;
}

export async function deferBookingWriteToOfflineIfNeeded(
  operation: 'create' | 'update' | 'delete',
  booking: Booking
): Promise<boolean> {
  if (!isBrowserOffline()) return false;
  const data =
    operation === 'delete'
      ? { booking: { id: booking.id } as Booking, operation: 'delete' as const }
      : { booking, operation };
  await offlineStorage.queueMutation({ type: 'booking', data, timestamp: Date.now() });
  mergeBookingLocal(
    booking,
    operation === 'create' ? 'add' : operation === 'update' ? 'update' : 'delete'
  );
  return true;
}
